import React, { useCallback, useEffect, useRef, useState } from 'react'
import { PROFILE, NAV, TESTIMONIALS, ABOUT_TEXT, EXPERIENCE, FACTS, AWARDS, INTEREST_TEXT, TOOL_GROUPS, PROJECT_GROUPS, OS_LIST } from '../Images/Json/Content.js'
import {
    Menu, X, GitBranch, Mail, MapPin, GraduationCap, Send,
    Quote, Award, Sparkles, Building2,
    ArrowUpRight, CheckCircle2, Layers, FolderKanban,
} from "lucide-react";
import { FaGithub, FaLinkedin } from 'react-icons/fa';
import img1 from '../Images/4.jpg'
import Terminal from './Terminal.jsx';


function LandingPage() {
    const [activeSection, setActiveSection] = useState("about");
    const [menuOpen, setMenuOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [sent, setSent] = useState(false);

    const sectionRefs = useRef({});
    const registerSection = useCallback((id) => (el) => {
        if (el) sectionRefs.current[id] = el;
    }, []);

    /* testimonial carousel: auto-slides via rAF, but the user can drag/scroll it freely */
    const testiViewportRef = useRef(null);
    const testiPausedRef = useRef(false);
    const testiDraggingRef = useRef(false);
    const testiDragStartXRef = useRef(0);
    const testiDragStartScrollRef = useRef(0);
    const [testiDraggingUi, setTestiDraggingUi] = useState(false);



    useEffect(() => {
        const el = testiViewportRef.current;
        if (!el) return;
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const speed = 40; // px per second
        let last = null;
        let raf;

        const wrap = () => {
            const half = el.scrollWidth / 2;
            if (half <= 0) return;
            if (el.scrollLeft >= half) el.scrollLeft -= half;
            if (el.scrollLeft < 0) el.scrollLeft += half;
        };

        const step = (t) => {
            if (last == null) last = t;
            const dt = (t - last) / 1000;
            last = t;
            if (!reduceMotion && !testiPausedRef.current && !testiDraggingRef.current) {
                el.scrollLeft += speed * dt;
                wrap();
            }
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, []);

    const handleTestiPointerDown = (e) => {
        const el = testiViewportRef.current;
        if (!el) return;
        testiDraggingRef.current = true;
        setTestiDraggingUi(true);
        testiDragStartXRef.current = e.clientX;
        testiDragStartScrollRef.current = el.scrollLeft;
        el.setPointerCapture?.(e.pointerId);
    };
    const handleTestiPointerMove = (e) => {
        if (!testiDraggingRef.current) return;
        const el = testiViewportRef.current;
        if (!el) return;
        const dx = e.clientX - testiDragStartXRef.current;
        el.scrollLeft = testiDragStartScrollRef.current - dx;
    };
    const endTestiDrag = () => {
        testiDraggingRef.current = false;
        setTestiDraggingUi(false);
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActiveSection(entry.target.dataset.section);
                });
            },
            { rootMargin: "-35% 0px -55% 0px", threshold: 0 }
        );
        Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && setSelectedProject(null);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const scrollTo = (id) => {
        setMenuOpen(false);
        const el = sectionRefs.current[id];
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleFormChange = (e) => {
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) return;
        setSent(true);
        setForm({ name: "", email: "", subject: "", message: "" });
        setTimeout(() => setSent(false), 5000);
    };

    const initials = PROFILE.name
        .split(" ")
        .map((w) => w[0])
        .join("");


    return (
        <React.Fragment>
            <div className="gr-bg" aria-hidden="true" />
            {/* navbar */}
            <nav className="gr-navbar">
                <div className="gr-brand">
                    <span className="gr-brand-mark">{initials}</span>
                    <span className="gr-brand-name">{PROFILE.name}</span>
                </div>
                <div className="gr-navlinks">
                    {NAV.map((n) => (
                        <button
                            key={n.id}
                            className={`gr-navlink${activeSection === n.id ? " active" : ""}`}
                            onClick={() => scrollTo(n.id)}
                        >
                            {n.label}
                        </button>
                    ))}
                </div>
                <button className="gr-burger" onClick={() => setMenuOpen(true)} aria-label="Open menu">
                    <Menu size={18} />
                </button>
            </nav>


            {menuOpen && (
                <>
                    <div className="gr-drawer-backdrop" onClick={() => setMenuOpen(false)} />
                    <div className="gr-drawer">
                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                            <button className="gr-burger" style={{ display: "flex" }} onClick={() => setMenuOpen(false)} aria-label="Close menu">
                                <X size={18} />
                            </button>
                        </div>
                        {NAV.map((n) => (
                            <button
                                key={n.id}
                                className={`gr-navlink${activeSection === n.id ? " active" : ""}`}
                                onClick={() => scrollTo(n.id)}
                            >
                                {n.label}
                            </button>
                        ))}
                    </div>
                </>
            )}

            <header className="gr-hero">
                <div>
                    {/* <div className="gr-eyebrow"><span className="bar" /> Full stack developer · {PROFILE.city}</div> */}
                    <h1>
                        Senior <span className="accent">Full Stack Developer</span>
                    </h1>
                    <p className="lede">
                        Building robust applications with clean architecture, reliable APIs, and scalable databases.
                    </p>
                    <div className="gr-meta-row">
                        <span className="gr-meta-item"><MapPin /> {PROFILE.city}</span>
                        {/* <span className="gr-meta-item"><GraduationCap /> {PROFILE.degree}</span> */}
                        <span className="gr-meta-item"><Mail /> {PROFILE.email}</span>
                    </div>
                    <div className="gr-cta-row">
                        <a className="gr-btn primary" href={`mailto:${PROFILE.email}`}>
                            <Send /> Get in touch
                        </a>
                        <button className="gr-btn ghost" onClick={() => scrollTo("projects")}>
                            <FolderKanban /> View projects
                        </button>
                    </div>
                </div>

                <Terminal />

                {/* <div className="gr-hero-visual gr-bracket" aria-hidden="true">
                    <svg viewBox="0 0 320 280">
                        <path className="gr-edge" d="M60,60 L160,40 L250,80" />
                        <path className="gr-edge" d="M60,60 L70,160" />
                        <path className="gr-edge" d="M160,40 L165,150" />
                        <path className="gr-edge" d="M250,80 L230,170" />
                        <path className="gr-edge flow" d="M70,160 L165,150 L230,170" />
                        <path className="gr-edge" d="M70,160 L120,230" />
                        <path className="gr-edge" d="M230,170 L180,230" />
                        <path className="gr-edge" d="M120,230 L180,230" />
                        <circle className="gr-node" cx="60" cy="60" r="9" />
                        <circle className="gr-node teal" cx="160" cy="40" r="7" />
                        <circle className="gr-node" cx="250" cy="80" r="9" />
                        <circle className="gr-node teal" cx="70" cy="160" r="8" />
                        <circle className="gr-node" cx="165" cy="150" r="10" />
                        <circle className="gr-node teal" cx="230" cy="170" r="8" />
                        <circle className="gr-node" cx="120" cy="230" r="7" />
                        <circle className="gr-node" cx="180" cy="230" r="7" />
                    </svg>
                    <div className="gr-visual-caption">
                      
                    </div>
                </div> */}
            </header>




            {/* ABOUT */}
            <section id="about" data-section="about" ref={registerSection("about")} className="gr-section">
                <div className="gr-section-head">
                    <span className="gr-section-num gr-mono">01</span>
                    <h2 className="gr-h2">About me</h2>
                </div>

                <div className="gr-profile-row">
                    <div className="gr-avatar-card gr-bracket">
                        <img src={img1} alt="Profile" className="gr-avatar" />
                        {/* <div className="gr-avatar"></div> */}
                        <h3>{PROFILE.fullName}</h3>
                        <div className="role">{PROFILE.role}</div>
                        <div className="gr-avatar-socials">
                            <a className="gr-icon-link" href={PROFILE.github} target="_blank" rel="noreferrer" aria-label="GitHub"><GitBranch /></a>
                            <a className="gr-icon-link" href={PROFILE.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn"><FaLinkedin /></a>
                            <a className="gr-icon-link" href={`mailto:${PROFILE.email}`} aria-label="Email"><Mail /></a>
                        </div>
                    </div>
                    <div>
                        {/* <ul className="gr-facts-list">
                            <li><span className="k">City</span><span className="v">{PROFILE.city}</span></li>
                            <li><span className="k">Degree</span><span className="v">{PROFILE.degree}</span></li>
                            <li><span className="k">Email</span><span className="v">{PROFILE.email}</span></li>
                        </ul> */}
                            <p className='text-parag'>{ABOUT_TEXT}</p>
                      
                    </div>
                </div>


                {/* experience */}
                <div className="gr-mini-head"><Layers /> Experience</div>
                <div className="gr-timeline">
                    {EXPERIENCE.map((job) => (
                        <div className="gr-tl-item" key={job.company + job.period}>
                            <div className="gr-tl-period gr-mono">{job.period}</div>
                            <h3>{job.role}</h3>
                            <div className="company"><Building2 />{job.company}</div>
                            <p className="desc">{job.description}</p>
                            <div className="gr-chip-row">
                                {job.stack.map((s) => <span className="gr-chip" key={s}>{s}</span>)}
                            </div>
                        </div>
                    ))}
                </div>



                {/* facts + awards */}
                <div className="gr-two-col">
                    <div>
                        <div className="gr-mini-head"><Sparkles /> Facts</div>
                        {FACTS.map((f, i) => <div className="gr-fact-block" key={i}>{f}</div>)}
                    </div>
                    <div>
                        <div className="gr-mini-head"><Award /> Licencses &amp; Certificates</div>
                        {AWARDS.map((a, i) => (
                            <div className="gr-award-item" key={i}>
                                <CheckCircle2 /> <a className="gr-award-link" href={a.link} target="_blank" rel="noopener noreferrer">{a.title}</a>
                            </div>
                        ))}
                    </div>
                </div>

                {/* <div className="gr-interest-block">
                    <div className="gr-mini-head">Interests</div>
                    <p>{INTEREST_TEXT}</p>
                </div> */}
            </section>



            {/* TESTIMONIAL */}
            <section className="gr-section">
                <div className="gr-section-head">
                    <span className="gr-section-num gr-mono">·</span>
                    <h2 className="gr-h2">Testimonials</h2>
                </div>
                <div
                    className={`gr-testi-viewport${testiDraggingUi ? " dragging" : ""}`}
                    ref={testiViewportRef}
                    onMouseEnter={() => { testiPausedRef.current = true; }}
                    onMouseLeave={() => { testiPausedRef.current = false; endTestiDrag(); }}
                    onPointerDown={handleTestiPointerDown}
                    onPointerMove={handleTestiPointerMove}
                    onPointerUp={endTestiDrag}
                >
                    <div className="gr-testi-track">
                        {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
                            <div className="gr-testi-card gr-bracket" key={i} aria-hidden={i >= TESTIMONIALS.length}>
                                <Quote className="quote-icon" />
                                <p className="q">"{t.quote}"</p>
                                <div className="gr-testi-who">
                                    <span className="n">{t.name}</span>
                                    <span className="r">{t.role}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>


            {/* CONTACT */}
            {/* <section className="gr-section">
                <div className="gr-section-head">
                    <span className="gr-section-num gr-mono">02</span>
                    <h2 className="gr-h2">Drop a message</h2>
                </div>
                <div className="gr-contact-grid">
                    <p className="gr-contact-note">
                        Have a project in mind, or a system that needs rebuilding? Send a few details and I'll get back
                        to you at {PROFILE.email}.
                    </p>
                    <form className="gr-form" onSubmit={handleSubmit}>
                        <div className="gr-field-row">
                            <div className="gr-field">
                                <label htmlFor="gr-name">Name</label>
                                <input id="gr-name" name="name" value={form.name} onChange={handleFormChange} placeholder="Your name" required />
                            </div>
                            <div className="gr-field">
                                <label htmlFor="gr-email">Email</label>
                                <input id="gr-email" type="email" name="email" value={form.email} onChange={handleFormChange} placeholder="you@example.com" required />
                            </div>
                        </div>
                        <div className="gr-field">
                            <label htmlFor="gr-subject">Subject</label>
                            <input id="gr-subject" name="subject" value={form.subject} onChange={handleFormChange} placeholder="What's this about?" />
                        </div>
                        <div className="gr-field">
                            <label htmlFor="gr-message">Message</label>
                            <textarea id="gr-message" name="message" value={form.message} onChange={handleFormChange} placeholder="Tell me a bit more..." required />
                        </div>
                        <div>
                            <button className="gr-btn primary" type="submit"><Send /> Submit</button>
                            {sent && (
                                <div className="gr-sent-msg"><CheckCircle2 /> Message noted — thanks, I'll reply soon.</div>
                            )}
                        </div>
                    </form>
                </div>
            </section> */}


            {/* PROJECTS */}
            <section id="projects" data-section="projects" ref={registerSection("projects")} className="gr-section">
                <div className="gr-section-head">
                    <span className="gr-section-num gr-mono">03</span>
                    <h2 className="gr-h2">Projects</h2>
                </div>
                <p style={{ color: "var(--muted)", fontSize: 14, marginTop: -18, marginBottom: 30 }}>
                    Grouped by where the work happened. Click any project for a closer look.
                </p>
                {PROJECT_GROUPS.map((group) => (
                    <div className="gr-group" key={group.company}>
                        <div className="gr-group-head">
                            <Building2 />
                            <h3>{group.company}</h3>
                            <span className="role">— {group.role}</span>
                        </div>
                        <div className="gr-project-grid">
                            {group.projects.map((project, i) => (
                                <button
                                    key={project.title}
                                    className="gr-project-card"
                                    onClick={() => setSelectedProject({ ...project, company: group.company, role: group.role, index: i + 1 })}
                                >
                                    <div className="gr-p-top">
                                        <span className="gr-p-badge gr-mono">{String(i + 1).padStart(2, "0")} · {project.type}</span>
                                        <span className="go"><ArrowUpRight /></span>
                                    </div>
                                    <p>{project.title}</p>
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </section>


            {/* TOOLS + OS */}
            <section id="tools" data-section="tools" ref={registerSection("tools")} className="gr-section">
                <div className="gr-section-head">
                    <span className="gr-section-num gr-mono">04</span>
                    <h2 className="gr-h2">Tools &amp; programming language</h2>
                </div>
                <div className="gr-tools-grid">
                    {TOOL_GROUPS.map((g) => (
                        <div className="gr-tool-group" key={g.group}>
                            <h4>{g.group}</h4>
                            <div className="gr-chip-row">
                                {g.items.map((item) => <span className="gr-chip" key={item}>{item}</span>)}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="gr-mini-head" style={{ marginTop: 48 }}>Operating systems used</div>
                <div className="gr-os-grid">
                    {OS_LIST.map((os) => (
                        <div className="gr-os-card" key={os.name}>
                            <h4>{os.name}</h4>
                            <p>{os.detail}</p>
                        </div>
                    ))}
                </div>
            </section>


            <footer className="gr-footer">
                <span>© {new Date().getFullYear()} {PROFILE.name}</span>
                <span className="socials">
                    <a className="gr-icon-link" href={PROFILE.github} target="_blank" rel="noreferrer" aria-label="GitHub"><FaGithub /></a>
                    <a className="gr-icon-link" href={PROFILE.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn"><FaLinkedin /></a>
                </span>
            </footer>


            {/* project modal */}
            {selectedProject && (
                <div className="gr-modal-backdrop" onClick={() => setSelectedProject(null)}>
                    <div className="gr-modal gr-bracket" onClick={(e) => e.stopPropagation()}>
                        <div className="gr-modal-top">
                            <div>
                                <span className="badge gr-mono">{String(selectedProject.index).padStart(2, "0")} · {selectedProject.type}</span>
                                <h3 style={{ marginTop: 8 }}>{selectedProject.title}</h3>
                            </div>
                            <button className="gr-modal-close" onClick={() => setSelectedProject(null)} aria-label="Close">
                                <X size={16} />
                            </button>
                        </div>
                        <p className="company"><Building2 size={14} style={{ display: "inline", marginRight: 6, verticalAlign: -2 }} />{selectedProject.company} — {selectedProject.role}</p>
                        <p className="hint"></p>
                    </div>
                </div>
            )}


        </React.Fragment >
    )
}

export default LandingPage